import { tt } from '@agos/shared'

import type { BoardOrder } from './board'

/**
 * External couriers on the dispatch board - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 E, owner O139.
 *
 * The owner's answer 6: *in a restaurant with its own drivers, an order reaches the pool only when staff offer it*.
 * So the board says which orders are waiting for a courier and which one took each, offers an order and takes the
 * offer back, and - at *Vyraziť* with an external driver - settles up (the dialog beside this file).
 *
 * The server is the authority on every one of these: it refuses an offer of an order with a driver, an order on the
 * road, and an order to be paid by card at the door. These rules decide what to **offer**, so a refusal is the rare
 * race rather than the usual answer.
 */

export const OFFER_PATH = (orderId: number): string => `/api/v1/orders/${String(orderId)}/offer`

/**
 * The two ways an order is paid at the door that an external courier cannot take - he carries no terminal (the
 * owner's answer 4: cash, card online, or the QR he shows). The server's `CourierFields.offerable` says the same.
 */
export const CARD_AT_THE_DOOR: readonly string[] = ['card_courier', 'card_terminal']

/** Statuses an order has left the restaurant in, or will never leave it from - nothing to offer. */
const GONE: readonly string[] = ['on_the_way', 'at_address', 'delivered', 'picked_up', 'cancelled', 'completed']

/** Waiting for a courier, taken by one, or neither. */
export type CourierState = 'waiting' | 'claimed' | null

export function courierStateOf(order: Pick<BoardOrder, 'offeredAt' | 'claimedAt' | 'driverId'>): CourierState {
  const hasDriver = typeof order.driverId === 'number'
  if (!hasDriver && typeof order.offeredAt === 'string' && order.offeredAt !== '') return 'waiting'
  if (hasDriver && typeof order.claimedAt === 'string' && order.claimedAt !== '') return 'claimed'
  return null
}

/**
 * What the card offers about the pool:
 *
 * - `offer` - *Ponúknuť externým*: a delivery with no driver, not yet offered, not on the road.
 * - `withdraw` - *Stiahnuť ponuku*: offered and nobody has it yet.
 * - `card-at-door` - not offered, with the reason: it is to be paid by card at the door.
 * - `null` - nothing to say (the module is off, it is not a delivery, it has a driver, it has left, or - §9.792,
 *   owner O144 - its channel keeps its orders to the restaurant's own drivers).
 */
export type OfferAction = 'offer' | 'withdraw' | 'card-at-door' | null

export function offerActionFor(
  order: Pick<BoardOrder, 'offeredAt' | 'driverId' | 'status' | 'paymentMethod'>,
  context: { couriers: boolean; driven: boolean; mayOffer: boolean; channelOffers: boolean },
): OfferAction {
  if (!context.couriers || !context.driven || !context.mayOffer) return null
  if (typeof order.driverId === 'number') return null
  if (GONE.includes(order.status ?? '')) return null
  if (typeof order.offeredAt === 'string' && order.offeredAt !== '') return 'withdraw'
  /* §9.792 - after the withdrawal, so an offer made before the channel was switched off can still be taken back:
     switching it off moves no order. And before the card-at-the-door sentence, which explains a refusal this
     channel would never ask about. */
  if (!context.channelOffers) return null
  if (CARD_AT_THE_DOOR.includes(order.paymentMethod ?? '')) return 'card-at-door'
  return 'offer'
}

/** The sentence under a card-at-door order, so nobody wonders why it cannot be offered. */
export function cardAtTheDoorNote(): string {
  return tt('Platí sa kartou pri dverách - externý šofér terminál nemá. Zmeňte platbu, ak ju má niesť.')
}

interface DriverWithKind {
  id?: number
  isExternal?: boolean | null
}

/** Whether the driver about to be sent out is an external courier - the drivers read says so. */
export function isExternalDriver(drivers: readonly DriverWithKind[], driverId: number | null | undefined): boolean {
  if (typeof driverId !== 'number') return false
  return drivers.some((driver) => driver.id === driverId && driver.isExternal === true)
}

/**
 * The runs just sent out that went with an external courier - each gets the settling dialog.
 *
 * **A claimed order counts as well as the drivers read**, because that read answers only drivers seen in the last
 * four hours: a courier whose name has aged out of it still claimed the order from the pool, and the claim is the
 * order's own fact.
 */
export function externalRuns(
  runs: readonly { driverId: number; orderIds: readonly number[] }[],
  drivers: readonly DriverWithKind[],
  claimedOrderIds: readonly number[] = [],
): { driverId: number; orderIds: readonly number[] }[] {
  return runs.filter(
    (run) => isExternalDriver(drivers, run.driverId) || run.orderIds.some((id) => claimedOrderIds.includes(id)),
  )
}
